import { ReactElement, useEffect, useState, useRef } from "react";
import { Button, Layout, Space, Table, Input, Avatar, message, Radio, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Highlighter from "react-highlight-words";
import firebase from "firebase/app";
import { SearchOutlined, UserOutlined, DeleteFilled, AreaChartOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { ContentHeader } from "../../components/GlobalStyles";
import { getAuthState, getClubState } from "../../redux/selectors";
import { RemoveConfirm } from "./RemoveConfirm";
import { SetPriorityModal } from "./SetPriorityModal";
import { EditUserModal } from "./EditUserModal";
import { UPDATE_USER_REQUEST, SET_LOADING_STATE, RESET_LOADING_STATE, EDIT_CLUB_SUCCESS } from "../../redux/constants";

const { Content } = Layout;

export function UserManagement(): ReactElement {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(getAuthState);
  const { clubs } = useSelector(getClubState);
  const [users, setUsers] = useState<any[]>([]);
  const [user, setUser] = useState<any>();
  const [visibleRemoveConfirm, setVisibleRemoveConfirm] = useState<boolean>(false);
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>();
  const [sortedUsers, setSortedUsers] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("all");
  const [visiblePriorityModal, setVisiblePriorityModal] = useState<boolean>(false);
  const [visibleEditModal, setVisibleEditModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch({ type: SET_LOADING_STATE });
    firebase.firestore().collection("users").orderBy("fullName").get()
      .then(querySnapshots => {
        const usersArray = querySnapshots.docs.map(doc => doc.data());
        setUsers(usersArray);
        dispatch({ type: RESET_LOADING_STATE });
      })
      .catch(e => {
        console.log(e);
        dispatch({ type: RESET_LOADING_STATE });
      })
  }, []);

  useEffect(() => {
    let mSortedUsers: any[] = [];
    if (filter === "users") {
      mSortedUsers = users.filter(u => u.id !== currentUser?.id && u.role === "regular");
    } else if (filter === "managers") {
      mSortedUsers = users.filter(u => u.id !== currentUser?.id && u.role === "manager");
    } else {
      mSortedUsers = users.filter(u => u.id !== currentUser?.id);
    }
    mSortedUsers = mSortedUsers.map(u => {
      const priorityClubs = clubs.filter((c: any) => { if (c.priority) return c.priority.findIndex((cp: string) => cp === u.id) > -1; return false; });
      return {
        ...u,
        priority: priorityClubs
      }
    })
    setSortedUsers(mSortedUsers);
  }, [users, clubs, filter])

  const onChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    // eslint-disable-next-line no-console
    console.log("params", pagination, filters, sorter, extra);
  };

  const changeRole = (record: any) => {
    const updatedValues = {
      ...record,
      role: record.role === "manager" ? "regular" : "manager",
    };
    dispatch({ type: SET_LOADING_STATE });
    firebase.firestore().collection('users').doc(record.id).update({ role: record.role === "manager" ? "regular" : "manager" })
      .then(() => {
        setUsers(origin => {
          const mUsers = [...origin];
          const userIndex = mUsers.findIndex(u => u.id === record.id);
          if (userIndex === -1) return mUsers;
          mUsers[userIndex] = { ...mUsers[userIndex], role: record.role === "manager" ? "regular" : "manager" };
          return mUsers;
        })
        dispatch({ type: RESET_LOADING_STATE });
      })
      .catch(e => {
        console.log(e);
        dispatch({ type: RESET_LOADING_STATE });
      })
  };

  const removeUser = (record: any) => {
    setUser(record);
    setVisibleRemoveConfirm(true);
  };

  const onDoneRemoveUser = (uid: string) => {
    setUser(null);
    setVisibleRemoveConfirm(false);
    setUsers(origin => origin.filter(u => u.id !== uid));
  }

  const handleSearch = (
    selectedKeys: string[],
    confirm: any,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: (props: any) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props;
      return (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: string, record: any) => {
      if (record[dataIndex])
        return record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      return "";
    },
    onFilterDropdownVisibleChange: (visible: boolean) => {
      // if (visible) {
      //   setTimeout(() => searchInput.current?, 100);
      // }
      console.log(searchInput.current);
    },
    render: (text: string) => {
      if (searchedColumn === dataIndex) {
        if (dataIndex === "email" || dataIndex === "phoneNumber") {
          return (
            <Button type="link" href={dataIndex === "email" ? `mailto:${text}` : `tel:${text}`}>
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ""}
              />
            </Button>
          );
        }
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        );
      }
      if (dataIndex === "email" || dataIndex === "phoneNumber") {
        return (
          <Button type="link" href={dataIndex === "email" ? `mailto:${text}` : `tel:${text}`}>
            {text}
          </Button>
        )
      }
      return text;
    },
  });

  const onSetPriority = (firestoreRef: string) => {
    firebase.firestore().collection('clubs').doc(firestoreRef).update({
      priority: firebase.firestore.FieldValue.arrayUnion(user.id)
    })
      .then(() => {
        message.success(`${user.fullName} has been add to Priority List.`);
        const oldClubs = [...clubs];
        const clubIndex = oldClubs.findIndex((c: any) => firestoreRef === c.firestoreRef);
        oldClubs[clubIndex] = { ...oldClubs[clubIndex], priority: [...oldClubs[clubIndex].priority ?? [], user.id] };
        dispatch({ type: EDIT_CLUB_SUCCESS, payload: { allClubs: oldClubs } });
        setVisiblePriorityModal(false);
        setUser(null);
      })
      .catch(e => {
        console.log(e);
        message.error('Oop! Something went wrong!');
        setVisiblePriorityModal(false);
        setUser(null);
      })
  }
  const onRemovePriority = (e: any, priorityClub: any, userId: string) => {
    e.preventDefault();
    console.log(priorityClub.firestoreRef);
    console.log(userId);
    firebase.firestore()
      .collection("clubs")
      .doc(priorityClub.firestoreRef)
      .update({ priority: firebase.firestore.FieldValue.arrayRemove(userId) })
      .then(() => {
        const oldClubs = [...clubs];
        const clubIndex = oldClubs.findIndex((c: any) => priorityClub.firestoreRef === c.firestoreRef);
        oldClubs[clubIndex] = { ...oldClubs[clubIndex], priority: (oldClubs[clubIndex].priority ?? []).filter(uid => uid !== userId) };
        dispatch({ type: EDIT_CLUB_SUCCESS, payload: { allClubs: oldClubs } });
      })
      .catch(() => {
        console.log("Error");
      });

  }

  const onAddPriority = (record: any) => {
    setUser(record);
    setVisiblePriorityModal(true);
  }

  const onClickEditBtn = (record: any) => {
    setUser(record);
    setVisibleEditModal(true);
  }

  const columns: any = [
    {
      title: "Photo",
      key: "photoUrl",
      render: (record: any) => (
        <Avatar src={record.photoUrl} size={30} icon={<UserOutlined />} />
      ),
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      ...getColumnSearchProps("phoneNumber"),
    },
    {
      title: "Gender",
      key: "gender",
      render: (record: any) => (
        <span style={{ textTransform: "capitalize" }}>{record.gender}</span>
      )
    },
    {
      title: "Instagram",
      dataIndex: "instagram",
      key: "instagram",
    },
    {
      title: "Role",
      key: "role",
      render: (record: any) => (
        <Tag style={{ width: 60, textAlign: "center" }} color={record.role === "regular" ? "#87d068" : "#108ee9"}>{record.role === "regular" ? "User" : "Manager"}</Tag>
      )
    },
    {
      title: "Priority",
      key: "priority",
      render: (record: any) => {
        return (
          <Space style={{ width: 200, flexWrap: "wrap" }}>
            {record.priority.map((p: any) => (
              <Tag
                closable
                key={p}
                onClose={(e) => onRemovePriority(e, p, record.id)}
                color="purple">{p.name}</Tag>))}
            <button
              type="button"
              style={{ border: 0, width: "auto", background: "none", padding: 0, cursor: "pointer" }}
              onClick={() => onAddPriority(record)}
            >
              <Tag><PlusOutlined style={{ fontSize: 10 }} /> Add</Tag>
            </button>
          </Space>
        )
      }
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => {
        return (
          <Space size="middle">
            <Button
              style={{ width: 85, fontSize: 12, padding: 0, height: 30 }}
              type="primary"
              onClick={() => changeRole(record)}
              icon={record.role === "manager" ? <UserOutlined /> : <AreaChartOutlined />}
              shape="round"
            >
              {record.role === "manager"
                ? "Set as User"
                : "Set as CM"}
            </Button>
            <Button
              type="primary"
              onClick={() => onClickEditBtn(record)}
              style={{ width: 85, fontSize: 12, padding: 0, height: 30, }}
              shape="round"
              icon={<EditOutlined />}
            >
              Edit
            </Button>
            <Button
              style={{ width: 85, fontSize: 12, padding: 0, height: 30 }}
              type="primary"
              danger
              onClick={() => removeUser(record)} icon={<DeleteFilled />}
              shape="round"
            >
              Delete
            </Button>
          </Space>
        )
      }
    },

  ];

  const onUpdatedUser = (updatedUser: any) => {
    setUsers(origin => {
      const beforeUsers = [...origin];
      const index = beforeUsers.findIndex(u => u.id === updatedUser.id);
      beforeUsers[index] = updatedUser;
      return beforeUsers;
    })
  }

  return (
    <Content>
      <ContentHeader style={{ justifyContent: "space-between" }}>Users
        <Radio.Group value={filter} onChange={(event) => setFilter(event.target.value)}>
          <Radio.Button value="all">All Users</Radio.Button>
          <Radio.Button value="users">Users</Radio.Button>
          <Radio.Button value="managers">Managers</Radio.Button>
        </Radio.Group>
      </ContentHeader>

      <Table
        columns={columns}
        dataSource={sortedUsers}
        onChange={onChange}
        pagination={false}
        loading={loading}
        rowKey={(record) => record.id}
      />
      {visibleRemoveConfirm && (
        <RemoveConfirm
          user={user}
          visible={visibleRemoveConfirm}
          onClose={() => setVisibleRemoveConfirm(false)}
          onDone={onDoneRemoveUser}
        />
      )}
      {
        (user && visiblePriorityModal) && (
          <SetPriorityModal
            visible={visiblePriorityModal}
            user={user}
            clubs={clubs}
            onClose={() => { setVisiblePriorityModal(false); setUser(null) }}
            onDone={onSetPriority}

          />
        )
      }
      {
        (user && visibleEditModal) && (
          <EditUserModal
            visible={visibleEditModal}
            user={user}
            onClose={() => { setVisibleEditModal(false); setUser(null) }}
            onDone={onUpdatedUser}
          />
        )
      }
    </Content>
  );
}
